/* Data sources (A4, P-ADM-121..131): super-admin only. */
import React from 'react';
import PropTypes from 'prop-types';
import { Button } from '../ui';
import { groupTenants } from '../../utils/adminConsole';
import { hasFailingStep } from '../AdminConsole/DataSourceCheck';
import {
    Alert, CheckPending, CheckResult, SectionHead, SourceChip, SourceEndpoint, Spinner,
} from './bits';
import { PlusIcon } from './icons';

function TenantRows({ tenant, t, canRepoint, onDetach }) {
    const busy = t.busyId === tenant.id;
    const result = t.checks[tenant.id];
    const blocked = hasFailingStep(result?.steps);
    return (
        <>
            <tr className={tenant.status === 'suspended' ? 'ad2-dim' : undefined}>
                <td>
                    <b>{tenant.org_name || '—'}</b>
                    <div className="ad2-slug">tenant/{tenant.slug}</div>
                </td>
                <td><SourceEndpoint tenant={tenant} /></td>
                <td><SourceChip tenant={tenant} /></td>
                <td>
                    <span className="ad2-row-acts">
                        <Button size="sm" disabled={busy} onClick={() => t.runCheck(tenant)} aria-label={`Check ${tenant.org_name || tenant.slug}`}>
                            {busy ? <><Spinner />Checking…</> : 'Check'}
                        </Button>
                        <Button
                            size="sm"
                            disabled={busy || tenant.status === 'active' || blocked}
                            title={blocked ? 'Resolve the failing checks first.' : undefined}
                            onClick={() => t.activate(tenant)}
                            aria-label={`Activate ${tenant.org_name || tenant.slug}`}
                        >
                            Activate
                        </Button>
                        {canRepoint && (
                            <Button size="sm" disabled={busy} onClick={() => t.setAttach({ dealerId: tenant.org_id })} aria-label={`Re-point ${tenant.org_name || tenant.slug}`}>
                                Re-point
                            </Button>
                        )}
                        <Button size="sm" className="ad2-btn-danger" disabled={busy} onClick={() => onDetach(tenant)} aria-label={`Detach ${tenant.org_name || tenant.slug}`}>
                            Detach
                        </Button>
                    </span>
                </td>
            </tr>
            {(busy || result) && (
                <tr className="ad2-chk-row">
                    <td colSpan={4}>{busy ? <CheckPending /> : <CheckResult result={result} />}</td>
                </tr>
            )}
        </>
    );
}
TenantRows.propTypes = {
    tenant: PropTypes.object.isRequired, t: PropTypes.object.isRequired, canRepoint: PropTypes.bool, onDetach: PropTypes.func.isRequired,
};

const Head = () => (
    <thead>
        <tr>
            <th scope="col"><span>Dealership</span></th>
            <th scope="col"><span>Source endpoint</span></th>
            <th scope="col"><span>Status</span></th>
            <th scope="col"><span className="v2-sr">Actions</span></th>
        </tr>
    </thead>
);

function SourcesSection({ orgs, t, onDetach }) {
    // Today's order: one row per tenant (as the API lists them), then the dealers without one.
    const {
        assigned: rows, unassigned, unattached, dealers,
    } = groupTenants(t.tenants, orgs);

    return (
        <>
            <SectionHead
                eyebrow="Platform"
                title="Data sources"
                actions={(
                    <Button variant="primary" onClick={() => t.setAttach({ dealerId: unattached[0]?.id })} disabled={dealers.length === 0}>
                        <PlusIcon />Connect source
                    </Button>
                )}
            >
                Where each dealership’s data is read from. The data team builds and registers the warehouse and hands you
                the coordinates — you connect to it and switch it on.
            </SectionHead>

            <Alert>{t.actionError || t.error}</Alert>

            <div className="v2-board ad2-board">
                <div className="v2-tscroll">
                    <table className="v2-table ad2-src" aria-label="Data sources">
                        <Head />
                        <tbody>
                            {rows.map((tenant) => (
                                <TenantRows key={tenant.id} tenant={tenant} t={t} canRepoint onDetach={onDetach} />
                            ))}
                            {unattached.map((o) => (
                                <tr key={o.id}>
                                    <td><b>{o.name}</b></td>
                                    <td className="ad2-muted">No source connected yet</td>
                                    <td><SourceChip tenant={null} /></td>
                                    <td>
                                        <span className="ad2-row-acts">
                                            <Button size="sm" onClick={() => t.setAttach({ dealerId: o.id })} aria-label={`Attach source to ${o.name}`}>Attach source</Button>
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {!t.loading && rows.length === 0 && unattached.length === 0 && unassigned.length === 0 && (
                        <div className="ad2-empty"><p>No dealerships yet.</p></div>
                    )}
                    {t.loading && <div className="ad2-loading" role="status">Loading data sources…</div>}
                </div>
                <div className="ad2-foot">
                    Sources are built and registered by the data team — this console only connects to what already exists.
                    A check that comes back amber can still be activated; the panels that depend on the missing datasets
                    simply stay empty until that data lands.
                </div>
            </div>

            {unassigned.length > 0 && (
                <section className="ad2-unassigned" aria-labelledby="ad2-unassigned-h">
                    <h2 id="ad2-unassigned-h" className="ad2-h2">Unassigned data sources</h2>
                    <p className="ad2-sub">
                        These sources point at an organization that no longer exists or is not a dealership. Check or detach
                        them here; nothing else lists them.
                    </p>
                    <div className="v2-board ad2-board">
                        <div className="v2-tscroll">
                            <table className="v2-table ad2-src" aria-label="Unassigned data sources">
                                <Head />
                                <tbody>
                                    {unassigned.map((tenant) => (
                                        <TenantRows key={tenant.id} tenant={tenant} t={t} canRepoint={false} onDetach={onDetach} />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
            )}
        </>
    );
}

SourcesSection.propTypes = {
    orgs: PropTypes.array.isRequired, t: PropTypes.object.isRequired, onDetach: PropTypes.func.isRequired,
};

export default SourcesSection;
